import { FC, useEffect, useState } from 'react'
import { parseIds, useSearchParams } from '@/hooks/useSearchParams'
import usePagination from '@/hooks/usePagination'
import Pagination from '@UI/New/Pagination'
import { reportAPI } from '@/api'
import CustomTable from '@common/CustomTable/Table'
import lessonTableStyles from '@pages/Admin/Reports/components/lessonTable.module.css'
import { IGroupAllLessonItem } from '../../utils/types'
import useGroupAllLessonsColumns from '../TableColumns/useGroupAllLessonsColumns'
import cn from '../GroupLessonReportTable/index.module.css'
import ChangeLessonReportStatusModal from '../MakeLessonReportModal/ChangeLessonReportStatusModal'
import EditEntityModal from '@modals/EditEntityModal/EditEntityModal'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import LessonStatusCards from '@pages/Admin/AllUsers/modals/EditStudentModal/components/LessonStatusesTab/LessonStatusCards'

interface IGroupAllLessonsTable {
  refreshKey?: string
  studentId?: number | string | null
  directionId?: string | null
  startDate?: string | null
  endDate?: string | null
  status?: string | null
  viewMode?: 'table' | 'cards'
  paginationContainer?: HTMLElement | null
}

const GroupAllLessonsTable: FC<IGroupAllLessonsTable> = ({
  refreshKey,
  studentId,
  directionId: selectedDirectionId,
  startDate,
  endDate,
  status,
  viewMode = 'table',
  paginationContainer,
}) => {
  const canEdit = useSelector((store: any) => {
    const role = store.profile?.profile?.role
    const isTeacher = ['seminarian', 'senior-seminarian'].includes(role)

    return !isTeacher || store.settings?.isTeacherCanEditStatuses === '1'
  })
  const [startDateFromSearch] = useSearchParams('all-start-date')
  const [endDateFromSearch] = useSearchParams('all-end-date')
  const [legacyClassId] = useSearchParams('all-class-id')
  const [classIds] = useSearchParams('classIds')
  const [classFolderIds] = useSearchParams('classFolderIds')
  const [directionId] = useSearchParams('direction-id')
  const [seminarianId] = useSearchParams('all-seminarian-id')
  const [statusFromSearch] = useSearchParams('all-status')
  const [search] = useSearchParams('all-search')
  const [franchiseIds] = useSearchParams('franchise-ids')
  const [editReport, setEditReport] = useState<IGroupAllLessonItem | null>(null)
  const [userModal, setUserModal] = useState<IUser | null>(null)
  const [sorting, setSorting] = useState(
    [
      'date-time',
      'seminarian',
      'course',
      'group',
      'students-count',
      'duration',
      'telebos-join-time',
      'telebos-late-minutes',
      'telebos-duration',
      'status',
      'comment',
      'salary',
    ].map((key) => ({ key, order: 'none' as 'asc' | 'desc' | 'none' })),
  )

  const currentSort = viewMode === 'cards'
    ? { key: 'date-time', order: 'asc' }
    : sorting.find((item) => item.order !== 'none')
  const effectiveStartDate = startDate ?? startDateFromSearch
  const effectiveEndDate = endDate ?? endDateFromSearch
  const effectiveStatus = status ?? statusFromSearch
  const effectiveDirectionId =
    selectedDirectionId === undefined ? directionId : selectedDirectionId

  const { state, loading, getState, ...params } = usePagination({
    api: reportAPI.group.allLessons,
    params: {
      start_date: effectiveStartDate,
      end_date: effectiveEndDate,
      ...(!studentId && classIds && classIds !== 'all'
        ? { class_ids: JSON.stringify(parseIds(classIds)) }
        : {}),
      ...(!studentId && classFolderIds && classFolderIds !== 'all'
        ? { class_folder_ids: JSON.stringify(parseIds(classFolderIds)) }
        : {}),
      ...(!studentId && legacyClassId && !classIds && !classFolderIds
        ? { class_id: legacyClassId }
        : {}),
      ...(effectiveDirectionId ? { direction_id: effectiveDirectionId } : {}),
      ...(!studentId && seminarianId ? { seminarian_id: seminarianId } : {}),
      ...(studentId ? { student_id: studentId } : {}),
      ...(effectiveStatus && effectiveStatus !== 'all'
        ? { status: effectiveStatus }
        : {}),
      ...(!studentId && search ? { search } : {}),
      ...(!studentId && franchiseIds ? { franchise_ids: franchiseIds } : {}),
      ...(currentSort
        ? { sort_key: currentSort.key, sort_order: currentSort.order }
        : {}),
    },
    firstRequest: false,
    latestRequestOnly: true,
    initialPerPage: 100,
  })

  useEffect(() => {
    getState({ page: 1 })
    params.setCurrentPage(1)
  }, [
    effectiveStartDate,
    effectiveEndDate,
    legacyClassId,
    classIds,
    classFolderIds,
    effectiveDirectionId,
    seminarianId,
    studentId,
    effectiveStatus,
    search,
    franchiseIds,
    currentSort?.key,
    currentSort?.order,
    refreshKey,
  ])

  const columns = useGroupAllLessonsColumns({
    onEdit: (report) => setEditReport(report),
    onSeminarianClick: (seminarian) => {
      if (!seminarian?.id) return
      setUserModal(seminarian)
    },
  })
  const data = state as IGroupAllLessonItem[]
  const pagination = <Pagination compact={!!paginationContainer} {...params} />

  return (
    <div className={cn.content}>
      {viewMode === 'cards' ? (
        <LessonStatusCards
          loading={loading}
          items={data.map((lesson) => ({
            id: lesson.id,
            dateTime: lesson.date_time,
            dateConference: lesson.date_conference,
            startTime: lesson.start_time,
            status: lesson.status,
            statusText: lesson.status_text,
            teacher: lesson.seminarian?.name,
            course: lesson.course_name,
            group: lesson.groups?.length
              ? lesson.groups.map((group) => group.name).join(', ')
              : lesson.group?.name,
            duration: lesson.duration,
            studentCount: lesson.student_count ?? lesson.students_count,
            salary: lesson.salary,
            comment: lesson.comment,
            isPaid: lesson.is_paid,
            hasEnoughBalance: lesson.has_enough_balance,
            onEdit: canEdit ? () => setEditReport(lesson) : undefined,
          }))}
        />
      ) : (
        <CustomTable<IGroupAllLessonItem>
          className={lessonTableStyles.table}
          flexColumns
          compact
          dense={Boolean(studentId)}
          dragScroll={Boolean(studentId)}
          data={data}
          columns={columns}
          loading={loading}
          sorting={sorting}
          onSortingClick={(key) => {
            setSorting((prev) =>
              prev.map((item) => ({
                ...item,
                order:
                  item.key === key
                    ? item.order === 'none'
                      ? 'asc'
                      : item.order === 'asc'
                      ? 'desc'
                      : 'none'
                    : 'none',
              })),
            )
          }}
        />
      )}
      {paginationContainer
        ? createPortal(pagination, paginationContainer)
        : pagination}
      <ChangeLessonReportStatusModal
        open={!!editReport}
        report={editReport as any}
        onSuccess={() => {
          getState()
          setEditReport(null)
        }}
        onDismiss={() => setEditReport(null)}
      />
      <EditEntityModal
        type="employee"
        open={!!userModal}
        employeeId={userModal?.id ?? null}
        onDismiss={() => setUserModal(null)}
        onUpdate={() => setUserModal(null)}
      />
    </div>
  )
}

export default GroupAllLessonsTable
